import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  Container,
  InputAdornment,
  IconButton,
  Fade,
  Slide,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AppContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ParticleBackground from '../../components/common/ParticleBackground';
import { LoginForm } from '../../types';
import { VALIDATION_RULES } from '../../utils/config';

// Validation schema
const validationSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

const LoginPage: React.FC = () => {
  const { login, loading, error, user, isVerified } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      setIsRedirecting(false); // Reset flag
      await login(data.email, data.password);

      // If we reach here, login was successful and email is verified
      // Navigate to clubs - ProtectedRoute will redirect to pending-approval if not verified
      navigate('/clubs', { replace: true });
    } catch (error: any) {
      // Check if the error is about email verification (emailVerified = false)
      if (error?.emailVerified === false) {
        // Email verification required - redirect to login OTP page
        setIsRedirecting(true);

        // Navigate to login OTP verification page
        navigate('/login-otp', {
          state: {
            email: error.email || data.email,
            name: error.name
          },
          replace: true // Replace history to prevent back button issues
        });
        return;
      }

      // Handle incorrect password - also redirect to OTP login
      if (error?.response?.status === 401 || error?.response?.data?.error === 'INVALID_PASSWORD') {
        setIsRedirecting(true);

        // Navigate to login OTP page for password-less login
        navigate('/login-otp', {
          state: {
            email: data.email,
            name: user?.name || ''
          },
          replace: true
        });
        return;
      }

      // Handle other specific errors
      if (error?.response?.status === 404 || error?.response?.data?.error === 'USER_NOT_FOUND') {
        // User not found - show custom message
        return; // Error already set by AppContext
      }
    }
  };

  return (
    <>
      <ParticleBackground />
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 4,
          }}
        >
        <Slide direction="down" in={true} timeout={600}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              border: '1px solid',
              borderColor: 'rgba(0, 0, 0, 0.06)',
            }}
          >
          {/* Header */}
          <Fade in={true} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                  letterSpacing: '-0.02em',
                }}
              >
                Unii Club
              </Typography>
              <Typography
                variant="h5"
                color="text.primary"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Welcome Back!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Sign in to your college community
              </Typography>
            </Box>
          </Fade>

          {/* Error message */}
          {error && !isRedirecting && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Login form */}
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <TextField
              {...register('email')}
              fullWidth
              label="Email Address"
              type="email"
              autoComplete="email"
              autoFocus
              error={!!errors.email}
              helperText={errors.email?.message}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              {...register('password')}
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              error={!!errors.password}
              helperText={errors.password?.message}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitting || loading}
              sx={{
                mb: 2,
                py: 1.75,
                fontWeight: 600,
                borderRadius: 2.5,
                fontSize: '1.1rem',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              {isSubmitting || loading ? <LoadingSpinner size={24} message="" /> : 'Sign In'}
            </Button>

            {/* Forgot password link */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                sx={{ textDecoration: 'none' }}
                onClick={() => navigate('/forgot-password')}
              >
                Forgot your password?
              </Link>
            </Box>

            {/* Switch to register */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/register')}
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  Sign up here
                </Link>
              </Typography>
            </Box>
          </Box>

        
        </Paper>
        </Slide>
      </Box>
    </Container>
    </>
  );
};

export default LoginPage;